/**
 * PreviewGallery — container for the worklist preview stage.
 *
 * Responsibilities:
 *  - Derives the ordered finding set from selectGalleryFindings (no cap)
 *  - Drives useFindingSlices for windowed slice loading
 *  - Advances a global cine tick; each tile ping-pongs over its own frame count
 *  - Renders PreviewGalleryView (CSS handles the scroll layout)
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { PreviewGalleryView, type TileState } from "./PreviewGalleryView";
import { FindingPreviewModal } from "./FindingPreviewModal";
import { useFindingSlices } from "../hooks/useFindingSlices";
import {
  selectGalleryFindings,
  validateGalleryShape,
  type GalleryFinding,
} from "../hooks/useStudyQueries";

import { CINE_INTERVAL_MS, pingPongIndex } from "../utils/cine";
export { pingPongIndex } from "../utils/cine";

interface PreviewGalleryProps {
  studyId: string;
  deviceId: string;
  predictions: Record<string, any>;
  passing: Set<string>;
  meta?: string;
  onOpenReport: () => void;
}

export const PreviewGallery: React.FC<PreviewGalleryProps> = ({
  studyId,
  deviceId,
  predictions,
  passing,
  meta,
  onOpenReport,
}) => {
  const [tick, setTick] = useState(0);
  // Expanded modal state — null means no modal open
  const [expandedFinding, setExpandedFinding] = useState<GalleryFinding | null>(null);
  const handleSelectFinding = useCallback((f: GalleryFinding) => setExpandedFinding(f), []);
  const handleCloseModal = useCallback(() => setExpandedFinding(null), []);

  // Derive the ordered finding set — all detected findings, no cap.
  const { findings } = useMemo(
    () => selectGalleryFindings(passing, predictions),
    [predictions, passing],
  );

  // Loud, structured shape-validation — once per study, DEV-only. Catches
  // device-engine output-format drift (null category, key-shape mismatch,
  // empty detected set) instead of silently rendering an empty gallery.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    for (const warning of validateGalleryShape(passing, predictions)) {
      console.warn(warning);
    }
    // Keyed on the study-scoped inputs so it fires when predictions actually
    // arrive (bbox is async — studyId alone would fire against an empty {} and
    // never re-run).
  }, [studyId, passing, predictions]);

  // Load windowed slices for each finding
  const sliceMap = useFindingSlices(studyId, deviceId, findings);

  // Cine timer — advances a global tick whenever ANY finding has >1 frame.
  // Each tile indexes its OWN frame count via pingPongIndex, so single-frame
  // tiles stay static while multi-frame tiles animate over the same tick.
  const maxFrames = useMemo(
    () => Math.max(1, ...Object.values(sliceMap).map((s) => s.frames.length)),
    [sliceMap],
  );

  useEffect(() => {
    if (maxFrames <= 1) return; // nothing to animate
    const id = setInterval(() => {
      setTick((prev) => prev + 1);
    }, CINE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [maxFrames]);

  // Build per-finding tile state { imageUrl, loading } for the view. Each finding
  // ping-pongs over its OWN frame count (lengths differ near series edges);
  // 1-frame tiles → frames[0]. `loading` drives the calm skeleton until the
  // first frame lands (and stays true for findings whose slices are still in flight).
  const tileStateByCondition = useMemo<Record<string, TileState>>(() => {
    const map: Record<string, TileState> = {};
    for (const finding of findings) {
      const state = sliceMap[finding.condition];
      const frames = state?.frames ?? [];
      const imageUrl = frames[pingPongIndex(tick, frames.length)] ?? frames[0] ?? null;
      map[finding.condition] = { imageUrl, loading: state?.loading ?? true };
    }
    return map;
  }, [findings, sliceMap, tick]);

  return (
    // Persistent shell — always present, never replaced with a spinner
    <div className="flex min-h-0 flex-1 flex-col">
      <PreviewGalleryView
        findings={findings}
        tileStateByCondition={tileStateByCondition}
        studyId={studyId}
        meta={meta}
        onOpenReport={onOpenReport}
        onSelectFinding={handleSelectFinding}
      />
      {expandedFinding && (
        <FindingPreviewModal
          finding={expandedFinding}
          studyId={studyId}
          deviceId={deviceId}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};
